import { Role } from '@/Enums/Users/Role.enum';
import {
  IClientControlOption,
  IControlOption,
  IGroupedControlOption,
  IWhitelabelFilterOption,
} from '@/Interfaces/ControlOption.interface';
import { IFilterModel, IFilterModelWithCampaigns } from '@/Interfaces/Filters/FilterModel.interface';
import {
  getAdvertiserIdsFromAdvertisers,
  getAdvertiserIdsFromClients,
  getAdvertiserIdsFromWhitelabels,
} from '../advertisers';
import { isRoleAllowedWhitelabelSelect } from '../roles';

export function getControlOptionsValues<T extends IControlOption<any, any>>(options: T[] = []): T['value'][] {
  return (options ?? []).flatMap(({ value }) => value).filter(Boolean);
}

export function getGroupedControlOptionsValues<T extends IGroupedControlOption<any, any, any>>(
  options: T[] = [],
): Record<string, T['value'][]> {
  const resultMap: Record<string, T['value'][]> = {};
  options.forEach(({ value, groupValue: group }) => {
    if (!resultMap[group]) {
      resultMap[group] = [];
    }
    if (value) {
      resultMap[group].push(value);
    }
  });
  return resultMap;
}

export const getLineItemsFilter = (filterPlacement: IControlOption[]) => getControlOptionsValues(filterPlacement);
export const getCampaignsFilter = (filterCampaigns: IControlOption[]) => getControlOptionsValues(filterCampaigns);

/** Deprecated due to falling back to all available advertisers on no selected filters
 * Use `getSelectedAdvertiserFilter` for new API
 */
export function getAdvertiserFilter<
  TAdvertiser extends IControlOption = IControlOption,
  TAdvertiserKey extends keyof TAdvertiser = keyof TAdvertiser,
  TFilter extends IFilterModel<TAdvertiser, TAdvertiser> = IFilterModel<TAdvertiser, TAdvertiser>,
>(
  filters: TFilter,
  role: Role,
  clients: IClientControlOption<TAdvertiser>[] = [],
  whitelabels: IWhitelabelFilterOption<TAdvertiser>[] = [],
  advertiserKey: TAdvertiserKey,
): TAdvertiser[TAdvertiserKey][] {
  // Clients (and whitelabels, via their nested clients) can list the same
  // advertiser more than once when flattened, so de-dupe before returning
  // (flank #4687).
  const dedupe = (ids: TAdvertiser[TAdvertiserKey][]) => Array.from(new Set(ids));

  if (filters.advertisers?.length) {
    return dedupe(getAdvertiserIdsFromAdvertisers<TAdvertiser, TAdvertiserKey>(filters.advertisers, advertiserKey));
  }
  if (filters.clients?.length) {
    return dedupe(getAdvertiserIdsFromClients<TAdvertiser, TAdvertiserKey>(filters.clients, advertiserKey));
  }
  if (isRoleAllowedWhitelabelSelect(role)) {
    return dedupe(
      getAdvertiserIdsFromWhitelabels<TAdvertiser, TAdvertiserKey>(
        filters.whitelabels?.length ? filters.whitelabels : whitelabels,
        advertiserKey,
      ),
    );
  }
  if (!filters.clients?.length) {
    return dedupe(getAdvertiserIdsFromClients<TAdvertiser, TAdvertiserKey>(clients, advertiserKey));
  }
  return [];
}

/** Deprecated due to falling back to all available advertisers on no selected filters
 * Use `getSelectedAdvertiserFilter` for new API
 */
export function getAdvertiserCampaignFilters<
  TAdvertiser extends IControlOption = IControlOption,
  TAdvertiserKey extends keyof TAdvertiser = keyof TAdvertiser,
>(
  filters: IFilterModelWithCampaigns<TAdvertiser, TAdvertiser>,
  role: Role,
  clients: IClientControlOption<TAdvertiser>[] = [],
  whitelabels: IWhitelabelFilterOption<TAdvertiser>[] = [],
  advertiserKey: TAdvertiserKey,
): { campaignIds?: number[]; advertiserIds?: TAdvertiser[TAdvertiserKey][] } {
  if (filters.campaigns?.length) {
    return { campaignIds: getCampaignsFilter(filters.campaigns) };
  }
  return {
    advertiserIds: getAdvertiserFilter<TAdvertiser, TAdvertiserKey>(filters, role, clients, whitelabels, advertiserKey),
  };
}

//#region new approach
export function getSelectedAdvertiserFilter<
  TAdvertiser extends IControlOption = IControlOption,
  TAdvertiserKey extends keyof TAdvertiser = keyof TAdvertiser,
  TFilter extends IFilterModel<TAdvertiser, TAdvertiser> = IFilterModel<TAdvertiser, TAdvertiser>,
>(filters: TFilter, role: Role, advertiserKey: TAdvertiserKey): TAdvertiser[TAdvertiserKey][] {
  if (filters.advertisers?.length) {
    return getAdvertiserIdsFromAdvertisers<TAdvertiser, TAdvertiserKey>(filters.advertisers, advertiserKey);
  }
  if (filters.clients?.length) {
    return getAdvertiserIdsFromClients<TAdvertiser, TAdvertiserKey>(filters.clients, advertiserKey);
  }
  if (isRoleAllowedWhitelabelSelect(role) && filters.whitelabels?.length) {
    return getAdvertiserIdsFromWhitelabels<TAdvertiser, TAdvertiserKey>(filters.whitelabels, advertiserKey);
  }
  return [];
}

export function getSelectedAdvertiserCampaignFilters<
  TAdvertiser extends IControlOption = IControlOption,
  TAdvertiserKey extends keyof TAdvertiser = keyof TAdvertiser,
>(
  filters: IFilterModelWithCampaigns<TAdvertiser, TAdvertiser>,
  role: Role,
  advertiserKey: TAdvertiserKey,
): { campaignIds?: number[]; advertiserIds?: TAdvertiser[TAdvertiserKey][] } {
  const advertiserIds = getSelectedAdvertiserFilter<TAdvertiser, TAdvertiserKey>(filters, role, advertiserKey);
  const campaignIds = filters.campaigns?.length ? getCampaignsFilter(filters.campaigns) : undefined;
  return {
    ...(advertiserIds?.length ? { advertiserIds } : {}),
    ...(campaignIds?.length ? { campaignIds } : {}),
  };
}
//#endregion
